import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import {
  useGetCustomerGroupParameterGroupQuery,
  useGetProductGroupParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { ProductList } from 'Services/ProductManagement/Features/Product/Components/ProductList'
import { DOMICILE_CLASS_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricing'
import {
  useProducts,
  useProductsWithParams,
  useSingleProduct
} from 'Services/ProductManagement/Features/Product/Hooks'
import { ProductDeleteModes } from 'Services/ProductManagement/Features/Product/productSlice'
import TariffProductDrawer from 'Services/ProductManagement/Features/Tariff/Components/TariffProductDrawer'
import { TariffProductsEdit } from 'Services/ProductManagement/Features/Tariff/Components/TariffProductsEdit'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import useSingleTariff from 'Services/ProductManagement/Features/Tariff/Hooks/useSingleTariff'
import {
  TariffProductsEditTabType,
  type TariffProductTabType
} from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import { isTariffEditAllowed } from 'Services/ProductManagement/Features/Tariff/utils'
import type { ProductTariffError } from 'Util/ApiClient'
import {
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useContext, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const TAB_PARAM_KEY = 'tab'
export const PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY = 'productDetailsDrawerTab'
const PRODUCTS_EDIT_DRAWER_TAB_PARAM_KEY = 'productsEditDrawerTab'

function TariffProductListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const { id: tariffId } = useParams()
  const { selectedTariffId } = useContext(TariffContext)

  const { singleTariff, singleLoading: singleTariffLoading } = useSingleTariff(tariffId)
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromParams = searchParams.get(TAB_PARAM_KEY) ?? ''
  const productsEditDrawerTabFromParams =
    searchParams.get(PRODUCTS_EDIT_DRAWER_TAB_PARAM_KEY) ?? TariffProductsEditTabType.PRODUCTS

  const {
    tariffProducts,
    tariffProductsLoading,
    params,
    setParams,
    total_results,
    selectedProductId,
    setSelectedProductId,
    fetchTariffProductsWithParams,
    fetchTariffProductsWithParamsAfterDeleteProduct
  } = useProductsWithParams({
    inTariff: tariffId
  })

  const { deleteProduct, deleteLoading: deleteProductLoading } = useSingleProduct(undefined)
  const [isTariffProductDrawerOpen, setIsTariffProductDrawerOpen] = useState(!!selectedProductId)
  const [isProductsEditDrawerOpen, setIsProductsEditDrawerOpen] = useState(false)

  const [searchText, setSearchText] = useState(params.search_text ?? '')

  const {
    data: productGroupParameterGroup = null,
    refetch: fetchProductGroups,
    isLoading: isProductGroupParameterGroupLoading
  } = useGetProductGroupParameterGroupQuery(undefined)
  const {
    data: customerGroupParameterGroup = null,
    refetch: fetchCustomerGroups,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery(undefined)

  const { fetchAllProductsWithoutPagination } = useProducts(false)

  useEffect(() => {
    fetchProductGroups()
    fetchCustomerGroups()
  }, [])

  useEffect(() => {
    setParams(prevParams => {
      if (selectedTariffId && prevParams.in_tariff !== selectedTariffId) {
        // Reset parameters back to default when changing tariff from sidebar's dropdown
        return {
          ...prevParams,
          in_tariff: selectedTariffId,
          sort_by: 'productCode',
          sort_order: SORT_ORDER_ASC,
          count: ROWS_PER_PAGE_DEFAULT,
          filter: undefined
        }
      }
      return prevParams
    })
  }, [selectedTariffId])

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: baseUrl
    },
    {
      title: singleTariff ? singleTariff.description : ''
    },
    {
      title: t`Tariff products`
    }
  ]

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handleTabChange = (_event: React.SyntheticEvent | null, newValue: string) => {
    setParams(prevState => {
      const newParams = { ...prevState, start_index: undefined }

      if (newValue) {
        newParams.filter = `productGroup Eq "${newValue}"`
      } else {
        // Remove filter if newValue is ''
        delete newParams.filter
        searchParams.delete('filter')
      }

      return newParams
    })
    searchParams.set(TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleTariffProductDrawerClose = () => {
    setSelectedProductId('')
    searchParams.delete(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY)
    searchParams.delete(DOMICILE_CLASS_PARAMETER_KEY)
    setSearchParams(searchParams)
    setIsTariffProductDrawerOpen(false)
  }

  const handleProductsEditDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: TariffProductTabType
  ) => {
    searchParams.set(PRODUCTS_EDIT_DRAWER_TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleProductsEditDrawerClose = () => {
    searchParams.delete(PRODUCTS_EDIT_DRAWER_TAB_PARAM_KEY)
    setSearchParams(searchParams)
    setIsProductsEditDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedProductId(id)
    setIsTariffProductDrawerOpen(true)
  }

  const handleProductsEditSaveSuccess = () => {
    fetchTariffProductsWithParams(params)
    fetchAllProductsWithoutPagination()
  }

  const handleProductDelete = (productId: string) => {
    deleteProduct(productId, { mode: ProductDeleteModes.CASCADE })
      .then(unwrapResult)
      .then(() => {
        setSelectedProductId('')
        setIsTariffProductDrawerOpen(false)
        fetchTariffProductsWithParamsAfterDeleteProduct(params)
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(({ message }: ProductTariffError) => {
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error deleting the product.`)
        }
      })
  }

  const productGroupOptions = productGroupParameterGroup?.parameters.map(
    ({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value,
      value: parameter_id
    })
  )
  // Add "All ticket types" option to the start of the array
  productGroupOptions?.unshift({ label: t`All ticket types`, value: '' })

  const isSingleTariffLoading =
    singleTariffLoading === 'pending' || deleteProductLoading === 'pending'
  const isLoading =
    tariffProductsLoading === 'pending' ||
    isSingleTariffLoading ||
    isCustomerGroupParameterGroupLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Tariff products`}</PageTitle>
      </Box>
      <ListActions>
        <Box>
          {isTariffEditAllowed(singleTariff) && (
            <CustomButton
              onClick={() => setIsProductsEditDrawerOpen(true)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Add products`}
            </CustomButton>
          )}
        </Box>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search by name or code`}
        />
      </ListActions>
      <TabContext value={tabFromParams}>
        <WithLoader loading={isProductGroupParameterGroupLoading} rows={1} height={33}>
          <TabList onChange={handleTabChange} aria-label={t`Product group tab list`}>
            {productGroupOptions?.map(({ label, value }) => (
              <Tab key={value} label={label} value={value} />
            ))}
          </TabList>
        </WithLoader>
        <Box pt={1}>
          {productGroupOptions?.map(({ value }) => (
            <TabPanel sx={{ p: 0 }} key={value} value={value}>
              <ProductList
                rows={tariffProducts}
                isLoading={isLoading}
                params={params}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={total_results}
                page={
                  params.start_index
                    ? Number.parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                customerGroupsParameter={customerGroupParameterGroup}
                allowSelect={false}
                selectedProductId={selectedProductId}
                onRowClick={handleRowClick}
              />
            </TabPanel>
          ))}
        </Box>
      </TabContext>
      {/* Product details drawer */}
      <TariffProductDrawer
        open={isTariffProductDrawerOpen}
        handleClose={handleTariffProductDrawerClose}
        handleProductDelete={handleProductDelete}
        productGroupParameterGroup={productGroupParameterGroup}
        isProductGroupParameterGroupLoading={isProductGroupParameterGroupLoading}
        customerGroupParameterGroup={customerGroupParameterGroup}
        isCustomerGroupParameterGroupLoading={isCustomerGroupParameterGroupLoading}
      />
      {/* Products edit drawer */}
      <DrawerRight open={isProductsEditDrawerOpen} onClose={handleProductsEditDrawerClose}>
        <TabContext value={productsEditDrawerTabFromParams}>
          <TabList
            onChange={handleProductsEditDrawerTabChange}
            aria-label={t`Tariff products tab list`}
          >
            <Tab label={t`Tariff products`} value={TariffProductsEditTabType.PRODUCTS} />
          </TabList>
          <Divider />
          <TabPanel sx={{ p: 0 }} value={TariffProductsEditTabType.PRODUCTS}>
            <WithLoader loading={isSingleTariffLoading || isProductGroupParameterGroupLoading}>
              {singleTariff && (
                <TariffProductsEdit
                  selectedTariff={singleTariff}
                  productGroupOptions={productGroupOptions}
                  handleCancel={handleProductsEditDrawerClose}
                  handleSaveSuccess={handleProductsEditSaveSuccess}
                />
              )}
            </WithLoader>
          </TabPanel>
        </TabContext>
      </DrawerRight>
    </Box>
  )
}

export default TariffProductListPage
